import React from 'react';
import styled from 'styled-components';
import { Flex, Typo, theme, Figure } from '../../../../ui';
import { useUser } from '../../../auth/hooks/useAuthState';
import { useFetchSettingsQuery } from '../../../settings/settingsApi';

const HeaderContainer = styled(Flex)`
  background: transparent;
  padding: 24px 0;
  margin-bottom: 32px;
  width: 100%;
`;

const ContentWrapper = styled(Flex)`
  width: 100%;
  gap: 32px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 24px;
  }
`;

const TextSection = styled(Flex)`
  flex: 1;
`;

const LogoSection = styled(Flex)`
  width: 200px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 150px;
    align-self: center;
  }
`;

const Greeting = styled(Typo.MediumText)`
  color: ${theme.colors.NEU_600};
  margin-bottom: 8px;
`;

const UserName = styled(Typo.PageHeader)`
  color: ${theme.colors.NEU_PRIMARY};
  margin-bottom: 4px;
  font-weight: 700;
`;

const BusinessName = styled(Typo.SectionHeader)`
  color: ${(props) => props.$brandColor || theme.colors.PRIMARY};
  font-weight: 600;
`;

const Tagline = styled(Typo.Info)`
  color: ${theme.colors.NEU_600};
  margin-top: 12px;
  font-style: italic;
`;

const PersonalizedHeader = ({ brandColor }) => {
  const user = useUser();
  const { data: settings = {} } = useFetchSettingsQuery();

  const getDisplayName = () => {
    if (settings.displayName) return settings.displayName;
    if (settings.firstName && settings.lastName) {
      return `${settings.firstName} ${settings.lastName}`;
    }
    if (settings.firstName) return settings.firstName;
    if (user?.displayName) return user.displayName;
    return 'there';
  };

  const getBusinessName = () => {
    if (user?.username) {
      return user.username.charAt(0).toUpperCase() + user.username.slice(1);
    }
    return null;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const displayName = getDisplayName();
  const businessName = getBusinessName();
  const greeting = getGreeting();
  const logoUrl = settings.theme?.logoUrl;

  return (
    <HeaderContainer direction='column' align='flex-start' $brandColor={brandColor}>
      <ContentWrapper align='center'>
        <TextSection direction='column' align='flex-start'>
          <Greeting>{greeting},</Greeting>
          <UserName>{displayName}</UserName>
          {businessName && <BusinessName $brandColor={brandColor}>{businessName}</BusinessName>}
          <Tagline>Let's make progress on your business goals today</Tagline>
        </TextSection>

        {logoUrl && (
          <LogoSection align='center' justify='center'>
            <Figure.Rectangle $objectfit='contain'>
              <img src={logoUrl} alt={businessName || 'Business Logo'} loading='lazy' />
            </Figure.Rectangle>
          </LogoSection>
        )}
      </ContentWrapper>
    </HeaderContainer>
  );
};

export default PersonalizedHeader;
